import { useCallback } from 'react';
import useSWR from 'swr';
import { parseISO, differenceInMinutes } from 'date-fns';
import { Dig } from './dig';
import { ANALYSIS_QUALITY_TYPES, ANALYSIS_HEADLINE } from './constants';
import { retrying, snakeToPascal } from './functions';

export const useAnalysis = (imageId, submissionIntent = undefined) => {
  let quality = ANALYSIS_QUALITY_TYPES.uncertain;
  let processing = false;
  const { data, error, mutate } = useSWR(
    imageId && { url: '/api/analysis', imageId },
    retrying(Dig.fetchAnalysis, { retries: 5, delay: 2000 }),
  );

  const startWeightAnalysis = (imageId, meta) =>
    Dig.analyzeWeight({ imageId, meta }).then(data => data);
  const revalidate = useCallback(() => mutate(), [mutate]);

  const isAcquisitionScreening =
    submissionIntent === 'weight_web_acquisition' ||
    data?.submissionIntent === 'weight_web_acquisition';

  if (data?.source) {
    const headline = data?.content.headline.toUpperCase();
    const isValidFieldName = `is${snakeToPascal(data?.type)}`;
    const ableToAssess = Boolean(data?.[isValidFieldName]);
    const isModelResult = data?.source.toUpperCase() === 'DL_MODEL';
    const isDental = data?.type === 'DENTAL';

    const isNotGoodAcquisitionScreening =
      isAcquisitionScreening &&
      headline !== ANALYSIS_HEADLINE.weightGood.toUpperCase() &&
      ableToAssess;

    const needsDelay =
      differenceInMinutes(Date.now(), parseISO(data?.createdAt)) <= 10 &&
      ableToAssess;

    const isInReview =
      headline === ANALYSIS_HEADLINE.reviewInProgress.toUpperCase();

    if (
      isModelResult &&
      (isNotGoodAcquisitionScreening || needsDelay || isInReview || isDental)
    ) {
      processing = true;
    }

    if (headline === ANALYSIS_HEADLINE.weightGood.toUpperCase()) {
      quality = ANALYSIS_QUALITY_TYPES.good;
    } else if (headline === ANALYSIS_HEADLINE.overweight.toUpperCase()) {
      quality = ANALYSIS_QUALITY_TYPES.neutral;
    } else if (headline === ANALYSIS_HEADLINE.underweight.toUpperCase()) {
      quality = ANALYSIS_QUALITY_TYPES.under;
    } else if (
      headline === ANALYSIS_HEADLINE.seekVeterinaryCare.toUpperCase()
    ) {
      quality = ANALYSIS_QUALITY_TYPES.bad;
    } else {
      quality = ANALYSIS_QUALITY_TYPES.uncertain;
    }
  }
  return {
    loading: imageId && !data && !error,
    data: !!data
      ? {
          ...data,
          quality,
          processing,
        }
      : undefined,
    error,
    startWeightAnalysis,
    revalidate,
  };
};
